import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';
import { env } from './env';

/**
 * Firebase configuration interface
 */
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

/**
 * Get Firebase configuration from environment variables
 *
 * In production, these values come from Vite environment variables
 * For local development, use Firebase emulator if available
 */
function getFirebaseConfig(): FirebaseConfig {
  const { apiKey, authDomain, projectId, storageBucket, messagingSenderId, appId } = env.firebase;

  if (!apiKey || !authDomain || !projectId || !storageBucket || !messagingSenderId || !appId) {
    throw new Error(
      'Missing Firebase configuration. Please check your environment variables. ' +
        'Required: VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, VITE_FIREBASE_PROJECT_ID, ' +
        'VITE_FIREBASE_STORAGE_BUCKET, VITE_FIREBASE_MESSAGING_SENDER_ID, VITE_FIREBASE_APP_ID'
    );
  }

  return {
    apiKey,
    authDomain,
    projectId,
    storageBucket,
    messagingSenderId,
    appId,
  };
}

// Initialize Firebase app
let app: FirebaseApp;
let auth: Auth;

try {
  const config = getFirebaseConfig();
  app = initializeApp(config);
  auth = getAuth(app);

  // Connect to Firebase emulator in development if configured
  if (import.meta.env.DEV) {
    const authEmulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST;
    if (authEmulatorHost) {
      connectAuthEmulator(auth, `http://${authEmulatorHost}`, { disableWarnings: true });
      console.info(`[Firebase] Using Auth emulator at ${authEmulatorHost}`);
    }
  }

  console.info('[Firebase] Initialized successfully');
} catch (error) {
  console.warn(
    '[Firebase] Initialization skipped — missing config. Auth features will not work.',
    error
  );
}

/**
 * Get the initialized Firebase app instance
 */
export function getFirebaseApp(): FirebaseApp {
  return app;
}

/**
 * Get the initialized Firebase Auth instance
 */
export function getFirebaseAuth(): Auth {
  return auth;
}

export { app, auth };
